import { DAYS_OF_WEEK } from './constants';
import { HOURS_MIN, RATIO_MAX, RATIO_MIN } from './constants';
import { ReserveCapacityFormValues, ReserveRow, RowFormValue, ViewMode } from './types';

export const isEmptyRow = (row: RowFormValue): boolean => !row.ratio && !row.reserved_until_hours;

const PERCENT_DECIMAL_PLACES = 2;
const RATIO_DECIMAL_PLACES = PERCENT_DECIMAL_PLACES + 2;

export const ratioToPercent = (ratio: number): number => {
	return parseFloat((ratio * 100).toFixed(PERCENT_DECIMAL_PLACES));
};

export const percentToRatio = (percent: number): number => {
	const ratio = percent / 100;
	const normalized = Math.min(RATIO_MAX, Math.max(RATIO_MIN, ratio));
	return parseFloat(normalized.toFixed(RATIO_DECIMAL_PLACES));
};

export const buildHoursFrequencyMap = (rows: RowFormValue[]): Map<number, number> => {
	const map = new Map<number, number>();

	for (const row of rows) {
		map.set(row.reserved_until_hours, (map.get(row.reserved_until_hours) ?? 0) + 1);
	}

	return map;
};

export const emptyRow = (): RowFormValue => ({ ratio: RATIO_MIN, reserved_until_hours: HOURS_MIN });

export const buildFormValues = (reserved_capacity: Omit<ReserveRow, 'id'>[]): ReserveCapacityFormValues => {
	const allDaysSource = reserved_capacity.filter(r => r.weekDay === undefined);
	const allDaysRows =
		allDaysSource.length > 0
			? allDaysSource.map(r => ({ ratio: ratioToPercent(r.ratio), reserved_until_hours: r.reserved_until_hours }))
			: [emptyRow()];

	const perDayRows = DAYS_OF_WEEK.map(({ weekDay }) => {
		const dayRows = reserved_capacity
			.filter(r => r.weekDay === weekDay)
			.map(r => ({ ratio: ratioToPercent(r.ratio), reserved_until_hours: r.reserved_until_hours }));

		return dayRows.length > 0 ? dayRows : [emptyRow()];
	});

	return { allDaysRows, perDayRows };
};

export const buildRowsForSave = (values: ReserveCapacityFormValues, viewMode: ViewMode): ReserveRow[] => {
	if (viewMode === ViewMode.allDays) {
		return values.allDaysRows
			.filter(row => !isEmptyRow(row))
			.map(row => ({ ratio: percentToRatio(row.ratio), reserved_until_hours: row.reserved_until_hours }));
	}

	return values.perDayRows.flatMap((rows, idx) => {
		const { weekDay } = DAYS_OF_WEEK[idx];

		return rows
			.filter(row => !isEmptyRow(row))
			.map(row => ({
				ratio: percentToRatio(row.ratio),
				reserved_until_hours: row.reserved_until_hours,
				weekDay
			}));
	});
};

export const isReserveRow = (d: unknown): d is ReserveRow => {
	return (
		Boolean(d) &&
		typeof (d as ReserveRow).ratio === 'number' &&
		typeof (d as ReserveRow).reserved_until_hours === 'number'
	);
};
